import Anthropic from '@anthropic-ai/sdk';
import type { AgentApi } from './api.ts';

/**
 * In-app mocap clean-up assistant (docs/AUTO_CLEANUP.md M4). A manual tool-use
 * loop over the SAME agent API exposed as window.aka: Claude inspects/analyzes
 * the scene, looks at rendered frames (returned as image blocks), and applies
 * keyable corrections. Runs entirely in the browser against the user's own
 * Anthropic API key — nothing goes through any other server.
 */

const MODEL = 'claude-opus-4-8';
const MAX_TURNS = 30; // tool-use round trips per user message

const SYSTEM_PROMPT = `You are a motion-capture clean-up assistant operating the AKA Mocap Viewer through its tool API.
The user is an animator; they will describe problems in plain language ("her left hand looks broken around 5 seconds", "make the handshake connect").

Rules:
- Call inspect first, then analyze to find suspect frame ranges before looking at pixels.
- ALWAYS checkpoint before your first mutation, and before risky experiments.
- Confirm every suspected defect visually with look (use the focus closeup) BEFORE changing anything, and verify every change with a before/after look at the identical frame and framing.
- Prefer keyed, blended corrections over constant ones; never touch frames outside the range you diagnosed. Small values first; escalate only after a verify-look.
- Times are frames at the take's fps (see inspect's take info). Angles are degrees, offsets metres.
- If a fix does not visibly improve the render, restore the checkpoint and say so rather than stacking more corrections.
- Tool cheat sheet: finger noise -> set_finger_fix (0.6-1.0); constant finger/thumb offsets -> set_finger_tune or key_finger_tune (thumbOut ~ -40 fixes VRM 0.x thumbs); flipped hands -> key_finger_tune with handFlipLeft/Right (+-180) or handTurnLeft/Right (+-90) keyed around the flip; intended contacts -> add_pin with influence keys ramping 0->1->0; ground penetration -> foot pin to a floor point or a small +y actor offset.
- End with: findings, the exact adjustments applied (tool, values, frames), and which checkpoint to restore to undo everything.`;

const obj = (properties: Record<string, unknown>, required: string[] = []) => ({
  type: 'object' as const,
  properties,
  required,
});
const num = (description: string) => ({ type: 'number', description });
const str = (description: string) => ({ type: 'string', description });

const FOCUS_PROPS = {
  frame: num('Frame to pose (defaults to playhead)'),
  focusActorId: str('Actor to frame a closeup on (e.g. "skeleton-1")'),
  focusBone: str('Canonical bone for the closeup, e.g. "righthand", "head", "leftfoot"'),
  focusDistance: num('Camera distance from the bone in metres (default 0.6)'),
  focusAzimuth: num('Degrees around the subject (default 30)'),
  focusElevation: num('Degrees above the subject (default 15)'),
};

const TOOLS: Anthropic.Tool[] = [
  {
    name: 'inspect',
    description:
      'Full JSON snapshot of the scene: take (fps, frames, marks, playhead), actors (ids, avatars, adjust offsets + keys, finger tune + keys), contact pins, finger-fix strength, cameras.',
    input_schema: obj({}),
  },
  {
    name: 'analyze',
    description:
      'Fast numeric scan (~100ms) over a frame range. Reports finger anomalies, hand-rotation flips, ground penetration, and inter-actor contact candidates — each with frame ranges and the suggested fixing tool. Call this before looking at pixels.',
    input_schema: obj({
      startFrame: num('Scan start (default: mark start)'),
      endFrame: num('Scan end (default: mark end)'),
    }),
  },
  {
    name: 'look',
    description:
      'Render one frame and return the image. Deterministic, gizmo-free, does not disturb the viewport. Use focusActorId+focusBone for a bone closeup; omit them to use the current camera.',
    input_schema: obj(FOCUS_PROPS),
  },
  {
    name: 'bone_world',
    description: 'World position + quaternion of an actor bone, optionally at a specific frame.',
    input_schema: obj(
      { actorId: str('Actor id'), bone: str('Canonical bone name'), frame: num('Optional frame') },
      ['actorId', 'bone']
    ),
  },
  {
    name: 'distance',
    description: 'Metres between two actor bones, optionally at a specific frame.',
    input_schema: obj(
      {
        actorId_a: str('First actor id'),
        bone_a: str('First bone'),
        actorId_b: str('Second actor id'),
        bone_b: str('Second bone'),
        frame: num('Optional frame'),
      },
      ['actorId_a', 'bone_a', 'actorId_b', 'bone_b']
    ),
  },
  {
    name: 'scrub',
    description: 'Move the playhead to a frame (the user sees this).',
    input_schema: obj({ frame: num('Target frame') }, ['frame']),
  },
  {
    name: 'set_finger_fix',
    description: 'Set the automated broken-finger healer strength, 0..1. Fixes noisy/popped finger joints globally.',
    input_schema: obj({ strength: num('0..1') }, ['strength']),
  },
  {
    name: 'set_finger_tune',
    description:
      'Set constant per-avatar finger/hand corrections (degrees): thumbOut, thumbRoll, thumbCurl, fingerCurl, fingerSplay, handFlipLeft, handFlipRight, handTurnLeft, handTurnRight. Only include fields you want to change.',
    input_schema: obj(
      {
        actorId: str('Actor id'),
        thumbOut: num('Thumb splay, degrees (VRM 0.x fix is ~ -40)'),
        thumbRoll: num('Thumb roll, degrees'),
        thumbCurl: num('Thumb curl, degrees'),
        fingerCurl: num('All-finger curl, degrees'),
        fingerSplay: num('Finger splay, degrees'),
        handFlipLeft: num('Left hand rotation around the forearm axis, degrees (tracker flip = +-180)'),
        handFlipRight: num('Right hand rotation around the forearm axis, degrees'),
        handTurnLeft: num('Left hand rotation around the palm normal, degrees (+-90 typical)'),
        handTurnRight: num('Right hand rotation around the palm normal, degrees'),
      },
      ['actorId']
    ),
  },
  {
    name: 'key_finger_tune',
    description:
      'Apply a finger-tune patch (same fields as set_finger_tune) and record it as a keyframe at the given frame. Keys blend smoothly — key the old value just before a defect and the correction at it, then key back after.',
    input_schema: obj(
      {
        actorId: str('Actor id'),
        frame: num('Frame to key at'),
        thumbOut: num('deg'),
        thumbRoll: num('deg'),
        thumbCurl: num('deg'),
        fingerCurl: num('deg'),
        fingerSplay: num('deg'),
        handFlipLeft: num('deg'),
        handFlipRight: num('deg'),
        handTurnLeft: num('deg'),
        handTurnRight: num('deg'),
      },
      ['actorId', 'frame']
    ),
  },
  {
    name: 'set_actor_adjust',
    description: 'Set an actor\'s world-space offset (metres) and/or scale. Only include fields to change.',
    input_schema: obj(
      { actorId: str('Actor id'), x: num('m'), y: num('m'), z: num('m'), scale: num('x1') },
      ['actorId']
    ),
  },
  {
    name: 'key_actor_offset',
    description: 'Apply an offset patch (same fields as set_actor_adjust) and key it at the given frame.',
    input_schema: obj(
      { actorId: str('Actor id'), frame: num('Frame'), x: num('m'), y: num('m'), z: num('m'), scale: num('x1') },
      ['actorId', 'frame']
    ),
  },
  {
    name: 'add_pin',
    description:
      'Add a contact pin: two-bone IK holding a hand/foot to a target. Target is either a world point or another actor\'s bone (with optional local-frame offset). Influence starts at 1 at the given frame; use set_pin_key to shape the envelope (ramp 0->1->0 around the contact).',
    input_schema: obj(
      {
        actorId: str('Actor whose limb is pinned'),
        limb: { type: 'string', enum: ['lefthand', 'righthand', 'leftfoot', 'rightfoot'] },
        pointX: num('World point target X (omit for bone target)'),
        pointY: num('World point Y'),
        pointZ: num('World point Z'),
        targetActorId: str('Bone target: actor id (omit for point target)'),
        targetBone: str('Bone target: canonical bone, e.g. "hips", "rightforearm"'),
        offsetX: num('Bone target: offset in the bone frame, m'),
        offsetY: num('m'),
        offsetZ: num('m'),
        frame: num('Frame where influence starts at 1 (default playhead)'),
      },
      ['actorId', 'limb']
    ),
  },
  {
    name: 'set_pin_key',
    description: 'Set a pin influence key (0..1) at a frame. 1 = locked to target, 0 = raw mocap; keys blend smoothly.',
    input_schema: obj(
      { pinId: str('Pin id'), frame: num('Frame'), value: num('Influence 0..1') },
      ['pinId', 'frame', 'value']
    ),
  },
  {
    name: 'delete_pin',
    description: 'Remove a contact pin.',
    input_schema: obj({ pinId: str('Pin id') }, ['pinId']),
  },
  {
    name: 'checkpoint',
    description: 'Save the full correction state (pins, tunes, offsets, cameras, marks). Returns an id for restore.',
    input_schema: obj({ label: str('Short label') }),
  },
  {
    name: 'restore',
    description: 'Restore a checkpoint — the undo system. Returns the restored snapshot.',
    input_schema: obj({ id: str('Checkpoint id') }, ['id']),
  },
];

export interface AssistantHooks {
  /** Streaming assistant text delta. */
  onText: (delta: string) => void;
  /** A new assistant message bubble is starting. */
  onMessageStart: () => void;
  /** A tool is being executed (for the activity log). */
  onTool: (name: string, input: Record<string, unknown>) => void;
  /** A look() happened — thumbnail data URL for the chat log. */
  onImage: (dataUrl: string) => void;
  onError: (message: string) => void;
  onDone: () => void;
}

export interface Assistant {
  send: (userText: string, hooks: AssistantHooks) => Promise<void>;
  abort: () => void;
  reset: () => void;
  busy: () => boolean;
}

export function createAssistant(aka: AgentApi, getApiKey: () => string): Assistant {
  let history: Anthropic.MessageParam[] = [];
  let running = false;
  let aborted = false;
  let activeStream: { abort: () => void } | null = null;

  const j = (v: unknown) => JSON.stringify(v);

  /** Execute one tool call against the aka API; returns tool_result content. */
  type ToolResultContent = string | Array<Anthropic.TextBlockParam | Anthropic.ImageBlockParam>;

  function runTool(
    name: string,
    input: Record<string, unknown>,
    hooks: AssistantHooks
  ): ToolResultContent {
    const n = (k: string) => input[k] as number | undefined;
    const s = (k: string) => input[k] as string | undefined;
    switch (name) {
      case 'inspect':
        return j(aka.inspect());
      case 'analyze':
        return j(aka.analyze({ startFrame: n('startFrame'), endFrame: n('endFrame') }));
      case 'look': {
        const focusActor = s('focusActorId');
        const focusBone = s('focusBone');
        const img = aka.look({
          frame: n('frame'),
          focus:
            focusActor && focusBone
              ? {
                  actorId: focusActor,
                  bone: focusBone,
                  distance: n('focusDistance'),
                  azimuth: n('focusAzimuth'),
                  elevation: n('focusElevation'),
                }
              : undefined,
        });
        hooks.onImage(img);
        return [
          {
            type: 'image',
            source: { type: 'base64', media_type: 'image/png', data: img.split(',')[1] },
          },
          { type: 'text', text: `Rendered frame ${n('frame') ?? 'at playhead'}.` },
        ];
      }
      case 'bone_world':
        return j(aka.boneWorld(s('actorId')!, s('bone')!, n('frame')));
      case 'distance':
        return j(
          aka.distance(
            { actorId: s('actorId_a')!, bone: s('bone_a')! },
            { actorId: s('actorId_b')!, bone: s('bone_b')! },
            n('frame')
          )
        );
      case 'scrub':
        return j(aka.scrub(n('frame') ?? 0).take);
      case 'set_finger_fix':
        return j(aka.setFingerFix(n('strength') ?? 0).fingerFix);
      case 'set_finger_tune': {
        const { actorId, ...patch } = input as { actorId: string } & Record<string, number>;
        const snap = aka.setFingerTune(actorId, patch);
        return j(snap.actors.find((a) => a.id === actorId)?.fingerTune ?? null);
      }
      case 'key_finger_tune': {
        const { actorId, frame, ...patch } = input as {
          actorId: string;
          frame: number;
        } & Record<string, number>;
        const snap = aka.keyFingerTune(actorId, frame, patch);
        const a = snap.actors.find((x) => x.id === actorId);
        return j({ fingerTune: a?.fingerTune, keys: a?.fingerTuneKeys.map((k) => k.time) });
      }
      case 'set_actor_adjust': {
        const { actorId, ...patch } = input as { actorId: string } & Record<string, number>;
        const snap = aka.setActorAdjust(actorId, patch);
        return j(snap.actors.find((a) => a.id === actorId)?.adjust ?? null);
      }
      case 'key_actor_offset': {
        const { actorId, frame, ...patch } = input as {
          actorId: string;
          frame: number;
        } & Record<string, number>;
        const snap = aka.keyActorOffset(actorId, frame, patch);
        const a = snap.actors.find((x) => x.id === actorId);
        return j({ adjust: a?.adjust, keys: a?.adjustKeys.map((k) => k.time) });
      }
      case 'add_pin': {
        const target =
          s('targetActorId') && s('targetBone')
            ? {
                kind: 'bone' as const,
                actorId: s('targetActorId')!,
                bone: s('targetBone')!,
                offset:
                  n('offsetX') || n('offsetY') || n('offsetZ')
                    ? ([n('offsetX') ?? 0, n('offsetY') ?? 0, n('offsetZ') ?? 0] as [
                        number,
                        number,
                        number,
                      ])
                    : undefined,
              }
            : {
                kind: 'point' as const,
                point: [n('pointX') ?? 0, n('pointY') ?? 0, n('pointZ') ?? 0] as [
                  number,
                  number,
                  number,
                ],
              };
        const frame = n('frame');
        const { pinId } = aka.addPin({
          actorId: s('actorId')!,
          limb: input.limb as 'lefthand' | 'righthand' | 'leftfoot' | 'rightfoot',
          target,
          keys: frame !== undefined ? [{ time: frame / stateFps(), value: 1 }] : undefined,
        });
        return j({ pinId });
      }
      case 'set_pin_key': {
        const snap = aka.setPinKey(s('pinId')!, n('frame')!, n('value')!);
        const pin = snap.contacts.find((p) => p.id === s('pinId'));
        return j(pin?.keys ?? null);
      }
      case 'delete_pin':
        aka.deletePin(s('pinId')!);
        return j({ deleted: true });
      case 'checkpoint':
        return j({ checkpointId: aka.checkpoint(s('label')) });
      case 'restore':
        aka.restore(s('id')!);
        return j({ restored: s('id') });
      default:
        throw new Error(`Unknown tool: ${name}`);
    }
  }

  // The pin key time conversion needs fps; read it via inspect (cheap).
  function stateFps(): number {
    return aka.inspect().take.fps;
  }

  async function send(userText: string, hooks: AssistantHooks): Promise<void> {
    if (running) return;
    const apiKey = getApiKey();
    if (!apiKey) {
      hooks.onError('Enter your Anthropic API key first.');
      return;
    }
    running = true;
    aborted = false;
    const client = new Anthropic({ apiKey, dangerouslyAllowBrowser: true });
    history.push({ role: 'user', content: userText });

    try {
      for (let turn = 0; turn < MAX_TURNS; turn++) {
        const stream = client.messages.stream({
          model: MODEL,
          max_tokens: 16000,
          thinking: { type: 'adaptive' },
          system: SYSTEM_PROMPT,
          tools: TOOLS,
          messages: history,
        });
        activeStream = stream;
        let started = false;
        stream.on('text', (delta) => {
          if (!started) {
            started = true;
            hooks.onMessageStart();
          }
          hooks.onText(delta);
        });
        const msg = await stream.finalMessage();
        activeStream = null;
        history.push({ role: 'assistant', content: msg.content });

        if (msg.stop_reason !== 'tool_use') {
          if (msg.stop_reason === 'refusal') hooks.onError('The model declined this request.');
          break;
        }
        if (aborted) break;

        const results: Anthropic.ToolResultBlockParam[] = [];
        for (const block of msg.content) {
          if (block.type !== 'tool_use') continue;
          hooks.onTool(block.name, block.input as Record<string, unknown>);
          let content: ToolResultContent;
          let isError = false;
          try {
            content = runTool(block.name, block.input as Record<string, unknown>, hooks);
          } catch (err) {
            content = err instanceof Error ? err.message : String(err);
            isError = true;
          }
          results.push({
            type: 'tool_result',
            tool_use_id: block.id,
            content,
            ...(isError ? { is_error: true } : {}),
          });
        }
        history.push({ role: 'user', content: results });
        if (aborted) break;
      }
    } catch (err) {
      if (!aborted) {
        // Surface typed SDK errors in plain language.
        if (err instanceof Anthropic.AuthenticationError) {
          hooks.onError('Invalid API key.');
        } else if (err instanceof Anthropic.RateLimitError) {
          hooks.onError('Rate limited — try again in a moment.');
        } else if (err instanceof Anthropic.APIError) {
          hooks.onError(`API error: ${err.message}`);
        } else {
          hooks.onError(err instanceof Error ? err.message : String(err));
        }
      }
      // Drop a dangling user turn so the next send starts from a valid history.
      while (history.length > 0 && history[history.length - 1].role === 'user') history.pop();
    } finally {
      running = false;
      activeStream = null;
      hooks.onDone();
    }
  }

  return {
    send,
    abort: () => {
      aborted = true;
      activeStream?.abort();
    },
    reset: () => {
      history = [];
    },
    busy: () => running,
  };
}
